/**
 * useFetchPageContent Hook - Dynamic Page Content Fetcher - By John Bellew - Infinia.ie - last updated 01/07/2025
 * 
 * Custom React hook for fetching dynamic page content from SharePoint with comprehensive
 * error handling, loading states, and flexible content format options. Provides automatic
 * refetching capabilities, fallback content management, and enhanced state tracking for
 * seamless content management across the application.
 * 
 * @fileoverview Custom hook for dynamic page content fetching with advanced options - By John Bellew - Infinia.ie - last updated 10/07/2025
 */

import { useEffect, useState } from 'react';
import apiHelper from '../utils/apiHelper';
import { FILE_NAMES } from '../utils/constants';

/**
 * Custom hook for fetching dynamic page content from SharePoint
 * 
 * Provides comprehensive content fetching functionality with automatic loading states,
 * error handling, manual refetching capabilities, and flexible content format options.
 * Supports both HTML and plain text content formats with customizable fallback content.
 * 
 * @param {number} pageId - The unique ID of the page content to fetch
 * @param {Object} options - Configuration options for content fetching
 * @param {boolean} options.returnPlainText - Return plain text instead of HTML (default: false)
 * @param {string} options.fallbackContent - Custom fallback content when no content found (default: "No content available")
 * 
 * @returns {Object} Hook return object with content and state management
 * @returns {string} returns.content - The fetched content (HTML or plain text)
 * @returns {boolean} returns.isLoading - Loading state indicator
 * @returns {string|null} returns.error - Error message if fetch failed
 * @returns {Function} returns.refetch - Manual refetch function
 * @returns {boolean} returns.hasContent - Whether valid content exists (not fallback)
 * @returns {boolean} returns.isEmpty - Whether content is empty or fallback
 * 
 * @example
 * // Basic usage with HTML content
 * const { content, isLoading, error } = useFetchPageContent(123);
 * 
 * @example
 * // Advanced usage with plain text and custom fallback
 * const { content, isLoading, refetch, hasContent } = useFetchPageContent(456, {
 *   returnPlainText: true,
 *   fallbackContent: "Custom fallback message"
 * });
 * 
 * @example
 * // Manual refetching with error handling
 * const { content, error, refetch } = useFetchPageContent(789);
 * if (error) {
 *   refetch(); // Manual retry
 * }
 */
const useFetchPageContent = (pageId, options = {}) => {
  const { 
    returnPlainText = false, 
    fallbackContent = "No content available" 
  } = options;

  // Better state management with descriptive names
  const [content, setContent] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Memoized fetch function for manual refetching
   * Handles content retrieval, format selection, and error management
   */
  const fetchContent = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Fetch the preloaded page content data
      const pageContentResult = await apiHelper.fetchJSONFileByNameAndContent(FILE_NAMES.PAGECONTENT);
      
      // Find the specific content by ID
      const targetContent = pageContentResult.fileContent.find(item => item.id === pageId);

      if (targetContent) {
        // Choose content format based on options
        const selectedContent = returnPlainText ? targetContent.plainConent : targetContent.htmlContent;
        setContent(selectedContent || fallbackContent);
      } else {
        setContent(fallbackContent);
      }
    } catch (err) {
      setError(err.message);
      setContent(fallbackContent);
      console.error('Error fetching page content:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Auto-fetch when pageId changes
  useEffect(() => {
    /**
     * Effect to automatically fetch content when pageId or format changes
     * Ensures content is always up-to-date with current parameters
     */
    if (pageId) {
      fetchContent();
    }
  }, [pageId, returnPlainText]); // Re-fetch if format preference changes too

  // Return enhanced object with all the goodies
  return {
    content,
    isLoading,
    error,
    refetch: fetchContent,
    // Bonus computed properties
    hasContent: content && content !== fallbackContent,
    isEmpty: !content || content === fallbackContent
  };
};

export default useFetchPageContent;
